import { useState } from 'react';

import { HelpCircle, Search } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface HelpSection {
  title: string;
  items: { heading: string; content: string }[];
}

const HELP_SECTIONS: HelpSection[] = [
  {
    title: 'Getting Started',
    items: [
      {
        heading: 'What are workflows?',
        content:
          'Workflows define what happens automatically after a phone call ends. They are visual graphs made of nodes (actions) connected by edges (arrows). The engine starts at the Start node and follows the arrows, executing each action in order.',
      },
      {
        heading: 'How to build a workflow',
        content:
          "Drag nodes from the palette at the top onto the canvas. Connect them by dragging from a node's output handle (right side) to another node's input handle (left side). Click a node to configure it in the right panel. Use Auto Layout to tidy the graph.",
      },
    ],
  },
  {
    title: 'Node Types',
    items: [
      {
        heading: 'Start (green)',
        content:
          'The entry point. Every workflow has exactly one Start node. It cannot be deleted or configured. Execution begins here.',
      },
      {
        heading: 'Condition (yellow)',
        content:
          'Evaluates a rule and branches into True or False paths. Configure a field, operator, and value. Supports compound rules with AND/OR logic — click "Add Rule" to combine multiple conditions.',
      },
      {
        heading: 'Send Email (blue)',
        content:
          'Sends an email. Configure recipients (To), subject, and body. You can load a template and map its variables to workflow data. Supports {{variable}} syntax in all text fields.',
      },
      {
        heading: 'Send SMS (purple)',
        content:
          'Sends an SMS message. Configure the recipient phone number and body text. Supports templates and {{variable}} syntax.',
      },
      {
        heading: 'Update Call (orange)',
        content:
          'Updates fields on the call record in the database. Add field/value mappings to set outcome, lead_data, or other call fields.',
      },
      {
        heading: 'Enrich Data (teal)',
        content:
          'Runs an enrichment action like scraping vehicle details. The result is added to the workflow context so downstream nodes can reference it.',
      },
      {
        heading: 'Run Review (rose)',
        content:
          'Executes an AI review using a selected review config. The review output is stored in the context at the Output Key (default: "review"). Chain multiple reviews by using different output keys. Use Input Mappings to pass upstream data into the review. Configure: Review Config (which AI review to run), Output Key (where to store results), and Additional Input (extra data to feed the AI).',
      },
      {
        heading: 'Call API (indigo)',
        content:
          'Makes an HTTP request to an external API. Configure method, URL, headers, and body. The response is stored at the Output Key (default: "api_response") for downstream nodes.',
      },
      {
        heading: 'End (red)',
        content:
          'Marks the end of a workflow branch. Optional — the workflow also ends when there are no more edges to follow.',
      },
    ],
  },
  {
    title: 'Conditions',
    items: [
      {
        heading: 'Available operators',
        content:
          'Equals, Not Equals — exact string match. Contains, Not Contains — substring check. Greater Than, Less Than — numeric comparison. Is Empty — true if undefined, null, or empty string. Is Not Empty — inverse. In List — comma-separated list, checks if value is in the list.',
      },
      {
        heading: 'Compound rules (AND/OR)',
        content:
          'Click "Add Rule" to create multiple conditions on one node. Use the AND/OR toggle to control logic: AND means all rules must match, OR means any rule must match. Remove rules to go back to single-rule mode.',
      },
      {
        heading: 'Truthiness rules',
        content:
          'For Is Empty: undefined, null, and empty string ("") are considered empty. Everything else (including "false", 0, empty arrays) is non-empty. For boolean fields, use Equals with value "true" or "false".',
      },
      {
        heading: 'Example: Send email only for leads',
        content:
          'Field: review.reviewed_outcome, Operator: Equals, Value: LEAD_CAPTURED. Connect the True branch to a Send Email node.',
      },
      {
        heading: 'Example: Check for callback request',
        content:
          'Field: review.additional_review_data.should_send_callback_email, Operator: Equals, Value: true. The True branch sends the callback notification email.',
      },
    ],
  },
  {
    title: 'Variables',
    items: [
      {
        heading: 'Basic syntax',
        content:
          '{{namespace.path}} — resolves to the value at that path. Example: {{contact.name}} resolves to "John Smith". Undefined values become empty strings.',
      },
      {
        heading: 'Fallback (||)',
        content:
          '{{a || b}} — if a is empty, use b. Chain multiple: {{a || b || "default"}}. Quoted strings are literal values.',
      },
      {
        heading: 'Ternary (?:)',
        content:
          '{{path ? "yes" : "no"}} — if path is truthy, output "yes", otherwise "no". Values can be quoted strings or other variable paths.',
      },
      {
        heading: 'Conditional blocks in templates',
        content:
          '{{#if variable}}...content...{{/if}} — renders the content only if the variable is truthy (non-empty, non-null, not false). Used in email HTML to conditionally show sections.',
      },
      {
        heading: 'Available namespaces',
        content:
          'call — id, direction, call_transport, status, outcome, duration, phone_number, lead_data, availability_data, callback_data, shift_data. contact — name, phone. campaign — name, id. config — notification_emails, high_intent_emails. review — populated by Run Review node: reviewed_outcome, lead_data, availability_data, additional_review_data, ai_analysis, confidence_score. enriched — populated by Enrich Data node.',
      },
    ],
  },
  {
    title: 'Templates',
    items: [
      {
        heading: 'What are templates?',
        content:
          'Reusable email/SMS content stored in the Templates page. When you select a template in a Send Email or Send SMS node, its body is loaded and you map template variables to workflow variables.',
      },
      {
        heading: 'Variable mapping',
        content:
          'Templates use simple variable names like {{customer_name}}. The mapping UI shows each template variable and lets you assign a workflow variable path (like review.lead_data.customer_name). At execution time, the workflow resolves the path and substitutes the value.',
      },
      {
        heading: '{{#if}} blocks in email templates',
        content:
          'Email templates can use {{#if variable}}...{{/if}} to conditionally render HTML sections. The variable is checked for truthiness — map it to a boolean or non-empty value in your workflow. These blocks do NOT appear in the variable mapping list.',
      },
    ],
  },
  {
    title: 'Review Input Mappings',
    items: [
      {
        heading: 'What are input mappings?',
        content:
          'Input mappings let you pass extra data from the workflow context into a Run Review node. The AI model receives this data alongside the call transcript, so it can use it in its analysis. This is how you chain reviews — review 2 can see the output of review 1.',
      },
      {
        heading: 'How they work',
        content:
          'Each mapping has a Context Label (the name the AI sees, like "prior_analysis") and a Workflow Variable (the data path, like "review.ai_analysis"). At execution time, the workflow engine resolves the variable path, gets the value, and appends it to the AI prompt under a section called "Additional Context from Workflow".',
      },
      {
        heading: 'Where does the data go?',
        content:
          'The mapped data is appended to the call transcript that gets sent to the AI model. It appears as formatted sections at the end of the transcript, each with the Context Label as a heading and the resolved value as the content. The AI can reference this data in its analysis.',
      },
      {
        heading: 'Example: Chain two reviews',
        content:
          'Review 1 (outputKey: "review") generates ai_analysis and lead_data. Review 2 (outputKey: "validation") has an input mapping: Context Label = "prior_review_analysis", Workflow Variable = "review.ai_analysis". The AI in review 2 now sees review 1\'s analysis and can build on it or validate it.',
      },
      {
        heading: 'Example: Pass enriched data to review',
        content:
          'An Enrich Data node scrapes vehicle details and stores them at "enriched". A Run Review node has an input mapping: Context Label = "vehicle_details", Workflow Variable = "enriched". The AI review now sees the scraped vehicle data and can reference it.',
      },
    ],
  },
  {
    title: 'Tips & Patterns',
    items: [
      {
        heading: 'Chaining reviews',
        content:
          'Use two Run Review nodes with different output keys (e.g., "review" and "validation"). The second review can receive data from the first via Input Mappings — map a context label to the first review\'s output path.',
      },
      {
        heading: 'Parallel branches',
        content:
          'Connect one node to multiple downstream condition nodes. Since the engine follows all outgoing edges from non-condition nodes, both branches execute independently. Example: after a review, check for both "is lead?" and "is callback?" in parallel.',
      },
      {
        heading: 'Enriching before emailing',
        content:
          'Place an Enrich Data node before a Send Email node. The enriched data (e.g., vehicle details) is added to the context, so your email template can reference fields like {{enriched.name}} or {{enriched.price}}.',
      },
      {
        heading: 'Import/Export workflows',
        content:
          'Use the Import/Export buttons in the toolbar to save workflows as JSON files or load them from files. Useful for sharing workflows between environments or backing them up.',
      },
    ],
  },
];

export const WorkflowHelpGuide = () => {
  const [filter, setFilter] = useState('');

  const filteredSections = filter
    ? HELP_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.heading.toLowerCase().includes(filter.toLowerCase()) ||
            item.content.toLowerCase().includes(filter.toLowerCase()),
        ),
      })).filter((section) => section.items.length > 0)
    : HELP_SECTIONS;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" title="Workflow Help Guide">
          <HelpCircle size={14} />
        </Button>
      </DialogTrigger>
      <DialogContent className="flex h-[80vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Workflow Help Guide</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search
            size={14}
            className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
          />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search help topics..."
            className="pl-9"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredSections.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No topics match &ldquo;{filter}&rdquo;
            </p>
          ) : (
            <Accordion type="multiple" className="w-full">
              {filteredSections.map((section) => (
                <AccordionItem key={section.title} value={section.title}>
                  <AccordionTrigger className="text-sm font-semibold">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {section.items.map((item) => (
                        <div key={item.heading}>
                          <p className="text-sm font-medium">{item.heading}</p>
                          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                            {item.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
